import { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import API from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import {
  BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';

const CHART_COLORS = { success: '#22C55E', fail: '#EF4444', users: '#3B82F6', accent: '#F59E0B' };

function StatCard({ emoji, label, value, sub, gradient }) {
  return (
    <div
      className={`relative overflow-hidden rounded-lg border border-border p-5 hover:-translate-y-1 hover:shadow-lg transition-all duration-300 group`}
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`absolute top-0 right-0 w-24 h-24 rounded-bl-full opacity-10 ${gradient}`} />
      <div className="flex items-start justify-between relative z-10">
        <div>
          <p className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-1">{label}</p>
          <p className="font-mono text-3xl sm:text-4xl font-bold tracking-tight">{value}</p>
          {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
        </div>
        <span className="text-3xl group-hover:scale-125 transition-transform duration-300">{emoji}</span>
      </div>
    </div>
  );
}

function BroadcastRow({ emoji, label, data }) {
  const total = (data?.success || 0) + (data?.fail || 0);
  const rate = total > 0 ? Math.round((data?.success / total) * 100) : 0;
  return (
    <div className="flex items-center justify-between py-3 border-b border-border/50 last:border-0 hover:bg-muted/30 px-3 -mx-3 rounded transition-colors">
      <div className="flex items-center gap-2">
        <span className="text-lg">{emoji}</span>
        <span className="text-sm font-medium">{label}</span>
      </div>
      <div className="flex items-center gap-5">
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-green-500" />
          <span className="font-mono text-sm font-semibold text-green-600 dark:text-green-400">{data?.success || 0}</span>
        </div>
        <div className="flex items-center gap-1.5">
          <span className="w-2 h-2 rounded-full bg-red-500" />
          <span className="font-mono text-sm font-semibold text-red-600 dark:text-red-400">{data?.fail || 0}</span>
        </div>
        <div className="min-w-[60px] text-right">
          <span className="font-mono text-xs px-2 py-0.5 rounded-full bg-muted">{rate}%</span>
        </div>
      </div>
    </div>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-popover border border-border rounded-lg p-3 shadow-lg text-xs">
      <p className="font-semibold mb-1.5">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />
          <span className="text-muted-foreground">{p.name}:</span>
          <span className="font-mono font-semibold">{p.value}</span>
        </div>
      ))}
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  const { selectedBot } = useOutletContext();
  const [stats, setStats] = useState(null);
  const [channelStats, setChannelStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [datewise, setDatewise] = useState(null);
  const [expandedDate, setExpandedDate] = useState(null);

  useEffect(() => {
    const params = {};
    if (selectedBot) params.bot_id = selectedBot;
    Promise.all([
      API.get('/dashboard/stats', { params }),
      API.get('/dashboard/channel-stats', { params }),
      API.get('/dashboard/users-datewise', { params: { ...params, days: 30 } })
    ]).then(([s, c, d]) => {
      setStats(s.data);
      setChannelStats(c.data);
      setDatewise(d.data.data || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [selectedBot]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <div className="flex items-center gap-3 text-muted-foreground">
        <span className="text-2xl animate-bounce">📊</span>
        <span>Loading dashboard...</span>
      </div>
    </div>
  );
  if (!stats) return <div className="text-center text-muted-foreground">Failed to load stats</div>;

  const pieData = [
    { name: 'Success', value: stats.broadcasts.today.success || 0 },
    { name: 'Failed', value: stats.broadcasts.today.fail || 0 },
  ];
  const hasPieData = pieData[0].value + pieData[1].value > 0;

  return (
    <div className="space-y-8" data-testid="dashboard-page">
      <div>
        <h1 className="text-3xl sm:text-4xl font-bold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>
          📊 Dashboard
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Real-time overview of your Telegram bot operations</p>
      </div>

      {user?.role === 'super_admin' && (
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <StatCard emoji="👥" label="Total Admins" value={stats.admins.total} gradient="bg-blue-500" />
          <StatCard emoji="✅" label="Active Admins" value={stats.admins.active} sub="Currently active" gradient="bg-green-500" />
          <StatCard emoji="⏸️" label="Inactive Admins" value={stats.admins.inactive} sub="Deactivated" gradient="bg-orange-500" />
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-5 gap-4">
        <StatCard emoji="🤖" label="Total Users" value={stats.users.total} sub="All bot users" gradient="bg-violet-500" />
        <StatCard emoji="🆕" label="Today" value={stats.users.today} sub="Joined today" gradient="bg-emerald-500" />
        <StatCard emoji="📅" label="Yesterday" value={stats.users.yesterday} sub="Joined yesterday" gradient="bg-sky-500" />
        <StatCard emoji="📆" label="Last 7 Days" value={stats.users.week} sub="This week" gradient="bg-amber-500" />
        <StatCard emoji="🗓️" label="Last 30 Days" value={stats.users.month} sub="This month" gradient="bg-rose-500" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">📡</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Broadcast Summary</h3>
              <p className="text-xs text-muted-foreground">Success & fail delivery counts</p>
            </div>
          </div>
          <BroadcastRow emoji="🕐" label="Today" data={stats.broadcasts.today} />
          <BroadcastRow emoji="📌" label="Yesterday" data={stats.broadcasts.yesterday} />
          <BroadcastRow emoji="📊" label="Last 7 Days" data={stats.broadcasts.week} />
          <BroadcastRow emoji="📈" label="Last 30 Days" data={stats.broadcasts.month} />
        </div>

        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">🎯</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Today's Delivery</h3>
              <p className="text-xs text-muted-foreground">Broadcast success rate</p>
            </div>
          </div>
          {hasPieData ? (
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={4} dataKey="value" strokeWidth={0}>
                  <Cell fill={CHART_COLORS.success} />
                  <Cell fill={CHART_COLORS.fail} />
                </Pie>
                <Tooltip content={<CustomTooltip />} />
                <Legend formatter={(v) => <span className="text-xs font-medium">{v}</span>} />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="flex flex-col items-center justify-center h-[220px] text-muted-foreground">
              <span className="text-4xl mb-2">📭</span>
              <span className="text-sm">No broadcasts sent today</span>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Today vs Yesterday vs Total comparison */}
        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-xl">📊</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>User Snapshot</h3>
              <p className="text-xs text-muted-foreground">Today vs Yesterday vs Total</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={[
              { name: 'Today', value: stats.users.today, fill: '#22C55E' },
              { name: 'Yesterday', value: stats.users.yesterday, fill: '#3B82F6' },
              { name: 'This Week', value: stats.users.week, fill: '#F59E0B' },
              { name: 'Total', value: stats.users.total, fill: '#8B5CF6' },
            ]} barSize={36}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="name" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="value" name="Users" radius={[6, 6, 0, 0]}>
                {[
                  { name: 'Today', fill: '#22C55E' },
                  { name: 'Yesterday', fill: '#3B82F6' },
                  { name: 'This Week', fill: '#F59E0B' },
                  { name: 'Total', fill: '#8B5CF6' },
                ].map((entry, i) => <Cell key={i} fill={entry.fill} />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-xl">👤</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Users Joined</h3>
              <p className="text-xs text-muted-foreground">Last 7 days trend</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={stats.chart_data} barSize={28}>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Bar dataKey="users" name="Users" fill={CHART_COLORS.users} radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="border border-border rounded-lg p-5">
          <div className="flex items-center gap-2 mb-5">
            <span className="text-xl">📢</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Broadcast Trend</h3>
              <p className="text-xs text-muted-foreground">Success vs Fail (7 days)</p>
            </div>
          </div>
          <ResponsiveContainer width="100%" height={260}>
            <AreaChart data={stats.chart_data}>
              <defs>
                <linearGradient id="gradSuccess" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.success} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLORS.success} stopOpacity={0} />
                </linearGradient>
                <linearGradient id="gradFail" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor={CHART_COLORS.fail} stopOpacity={0.3} />
                  <stop offset="95%" stopColor={CHART_COLORS.fail} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="hsl(var(--border))" vertical={false} />
              <XAxis dataKey="date" tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: 'hsl(var(--muted-foreground))' }} axisLine={false} tickLine={false} />
              <Tooltip content={<CustomTooltip />} />
              <Legend formatter={(v) => <span className="text-xs font-medium">{v}</span>} />
              <Area type="monotone" dataKey="success" name="Success" stroke={CHART_COLORS.success} fill="url(#gradSuccess)" strokeWidth={2} />
              <Area type="monotone" dataKey="fail" name="Failed" stroke={CHART_COLORS.fail} fill="url(#gradFail)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Channel Broadcasting Stats */}
      {channelStats && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="border border-border rounded-lg p-5">
            <div className="flex items-center gap-2 mb-4">
              <span className="text-xl">📢</span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Channel Broadcast</h3>
                <p className="text-xs text-muted-foreground">{channelStats.total_channels} channels connected</p>
              </div>
            </div>
            <BroadcastRow emoji="🕐" label="Today" data={channelStats.today} />
            <BroadcastRow emoji="📌" label="Yesterday" data={channelStats.yesterday} />
            <BroadcastRow emoji="📊" label="Last 7 Days" data={channelStats.week} />
            <BroadcastRow emoji="📈" label="Last 30 Days" data={channelStats.month} />
          </div>
          <div className="border border-border rounded-lg p-5">
            <div className="flex items-center gap-2 mb-5">
              <span className="text-xl">📺</span>
              <div>
                <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Channels Overview</h3>
                <p className="text-xs text-muted-foreground">Broadcast performance</p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Total Channels</p>
                <p className="font-mono text-2xl font-bold">{channelStats.total_channels}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Today Sent</p>
                <p className="font-mono text-2xl font-bold text-green-600 dark:text-green-400">{channelStats.today?.success || 0}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Week Total</p>
                <p className="font-mono text-2xl font-bold">{channelStats.week?.total || 0}</p>
              </div>
              <div className="border border-border rounded-md p-3 text-center">
                <p className="text-xs text-muted-foreground mb-1">Month Total</p>
                <p className="font-mono text-2xl font-bold">{channelStats.month?.total || 0}</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Date-wise User Joined */}
      {datewise && (
        <div className="border border-border rounded-lg p-4 sm:p-5" data-testid="datewise-users">
          <div className="flex items-center gap-2 mb-4">
            <span className="text-xl">📅</span>
            <div>
              <h3 className="text-lg font-semibold tracking-tight" style={{ fontFamily: 'Cabinet Grotesk' }}>Users Joined (Date-wise)</h3>
              <p className="text-xs text-muted-foreground">IST (UTC+5:30) — Day resets at 12:00 AM</p>
            </div>
          </div>
          <div className="space-y-1 max-h-[400px] overflow-y-auto">
            {datewise.map(d => (
              <div key={d.date} className="border border-border/50 rounded-md overflow-hidden">
                <button onClick={() => setExpandedDate(expandedDate === d.date ? null : d.date)}
                  className={`w-full flex items-center justify-between px-3 py-2 text-sm hover:bg-accent/30 transition-colors ${d.count > 0 ? 'cursor-pointer' : ''}`}>
                  <span className="font-medium">{d.display}</span>
                  <span className={`font-mono font-bold px-2 py-0.5 rounded-full text-xs ${d.count > 0 ? 'bg-green-500/10 text-green-600 dark:text-green-400' : 'bg-muted text-muted-foreground'}`}>
                    {d.count} user{d.count !== 1 ? 's' : ''}
                  </span>
                </button>
                {expandedDate === d.date && d.users?.length > 0 && (
                  <div className="border-t border-border/50 px-3 py-2 bg-muted/20 space-y-1">
                    {d.users.map((u, i) => (
                      <div key={i} className="flex items-center gap-2 text-xs">
                        <span className="font-medium">{u.first_name || 'Unknown'}</span>
                        {u.username && <span className="text-muted-foreground">@{u.username}</span>}
                        <span className="text-muted-foreground ml-auto font-mono">{u.joined_at?.slice(11, 16)}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}
